import { Check, ArrowRight, ShieldCheck, Building2, History, FileClock, Route, FolderLock, ClipboardCheck, X } from "lucide-react";
import { Container, Eyebrow, H2, Reveal, PrimaryCta } from "./ui";
import { DemandCard, LaptopMock } from "./Mockups";

const TRACK = ["o que enviou", "quando enviou", "quando a DPdigi recebeu", "o status", "o prazo informado", "mensagens", "documentos", "conclusão", "retorno da DPdigi"];

export function Traceability() {
  return (
    <section className="bg-[#03213E] py-20 sm:py-28 text-white" data-testid="lp-trace">
      <Container className="grid lg:grid-cols-2 gap-14 items-center">
        <Reveal>
          <Eyebrow light>Rastreabilidade</Eyebrow>
          <H2 light>Você não entrega o seu DP e fica no escuro.</H2>
          <p className="mt-6 text-lg text-sky-100/80">Na DPdigi, sua empresa acompanha o que está acontecendo. Cada demanda cria um histórico digital.</p>
          <div className="mt-8 text-sm font-semibold text-sky-300">Sua empresa consegue acompanhar:</div>
          <ul className="mt-4 grid grid-cols-2 gap-x-6 gap-y-2.5">{TRACK.map((t) => <li key={t} className="flex items-center gap-2 text-[15px] text-white/90"><Check className="h-4 w-4 text-sky-300 shrink-0" />{t}</li>)}</ul>
        </Reveal>
        <Reveal delay={120}><DemandCard dark /></Reveal>
      </Container>
    </section>);
}

const CHIPS = [[FileClock, "Protocolo"], [History, "Histórico"], [FolderLock, "Documentos"], [Route, "Status"], [Building2, "Responsável"], [ClipboardCheck, "Prazo"]];

export function Ledger() {
  return (
    <section className="bg-white py-20 sm:py-28" data-testid="lp-ledger">
      <Container className="grid lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-6">
          <Eyebrow>Lastro digital</Eyebrow>
          <H2>Menos “eu mandei”.<br /><span className="text-[#0B6FC2]">Mais “está registrado”.</span></H2>
          <p className="mt-6 text-lg leading-relaxed text-slate-600">As interações ficam organizadas no ambiente digital da DPdigi, criando histórico e rastreabilidade para a operação.</p>
          <p className="mt-4 text-lg leading-relaxed text-slate-600">Em vez de depender de informações espalhadas em diferentes canais, empresa e DP trabalham sobre um fluxo organizado de solicitações, respostas e documentos.</p>
        </Reveal>
        <div className="lg:col-span-6 grid grid-cols-2 sm:grid-cols-3 gap-3">{CHIPS.map(([Icon, l], i) => (
          <Reveal key={l} delay={i * 60} className="rounded-2xl border border-slate-200 bg-[#F8FAFC] p-5 transition-[border-color,transform] duration-200 hover:-translate-y-1 hover:border-[#0B6FC2]/40">
            <Icon className="h-5 w-5 text-[#0B6FC2]" /><div className="mt-6 font-mono text-xs font-semibold uppercase tracking-widest text-[#063C6D]">{l}</div>
          </Reveal>))}</div>
      </Container>
    </section>);
}

export function Portal() {
  return (
    <section id="tecnologia" className="bg-[#F0F7FF] py-20 sm:py-28 scroll-mt-20 overflow-hidden" data-testid="lp-portal">
      <Container>
        <Reveal className="max-w-3xl mx-auto text-center"><Eyebrow>Tecnologia</Eyebrow><H2>Seu Departamento Pessoal na tela do seu computador.</H2>
          <p className="mt-6 text-lg text-slate-600">Acesse funcionários, movimentações, solicitações, documentos, indicadores e retornos da DPdigi em um único ambiente.</p></Reveal>
        <Reveal delay={120} className="mt-14"><LaptopMock /></Reveal>
      </Container>
    </section>);
}

export function Control() {
  const P = [["DPdigi executa", "Especialistas cuidam da operação do seu DP."], ["Sua empresa acompanha", "Status, prazos e retornos à vista, a qualquer momento."], ["O sistema registra", "Cada solicitação, mensagem e documento fica no histórico."]];
  return (
    <section className="bg-white py-20 sm:py-28" data-testid="lp-control">
      <Container>
        <Reveal className="max-w-3xl"><Eyebrow>Controle</Eyebrow><H2>Terceirizar não significa perder o controle.</H2><p className="mt-5 text-lg text-slate-600">A DPdigi executa a operação. Sua empresa acompanha.</p></Reveal>
        <div className="mt-14 grid md:grid-cols-3 border-y border-slate-200 divide-y md:divide-y-0 md:divide-x divide-slate-200">{P.map(([t, d], i) => (
          <Reveal key={t} delay={i * 90} className="py-10 md:px-8 first:md:pl-0">
            <div className="font-display text-3xl sm:text-4xl font-bold text-[#063C6D] leading-tight">{t}.</div>
            <p className="mt-3 text-slate-600">{d}</p>
          </Reveal>))}</div>
        <p className="mt-8 font-display text-xl text-[#0B6FC2]">Terceirize a operação. Não o controle.</p>
      </Container>
    </section>);
}

const FOR = ["querem reduzir a burocracia do DP", "não querem montar uma grande estrutura interna", "querem profissionalizar seus processos", "precisam de mais controle", "querem centralizar informações", "desejam acompanhar as demandas", "querem transformar processos manuais em digitais"];

export function ForWho() {
  return (
    <section className="bg-[#F8FAFC] py-20 sm:py-28" data-testid="lp-forwho">
      <Container className="grid lg:grid-cols-12 gap-12">
        <Reveal className="lg:col-span-5">
          <Eyebrow>Para quem é</Eyebrow><H2>A DPdigi foi feita para empresas que...</H2>
          <div className="mt-8 rounded-2xl bg-[#063C6D] p-6 text-white"><div className="font-mono text-[11px] uppercase tracking-widest text-sky-300">Foco</div><div className="mt-2 font-display text-2xl font-semibold">Pequenas e médias empresas</div><p className="mt-2 text-sm text-sky-100/80">Que precisam de um DP profissional sem o peso de uma estrutura interna.</p></div>
        </Reveal>
        <ul className="lg:col-span-7 divide-y divide-slate-200 border-y border-slate-200">{FOR.map((f, i) => (
          <Reveal as="li" key={f} delay={i * 50} className="flex items-center gap-4 py-5"><span className="font-mono text-xs text-[#0B6FC2]">{String(i + 1).padStart(2, "0")}</span><span className="text-lg text-[#0F172A]">{f}</span></Reveal>))}</ul>
      </Container>
    </section>);
}

const BEFORE = ["WhatsApp", "E-mails", "Planilhas", "Arquivos espalhados", "Cobranças", "Dúvidas sobre andamento", "Processos manuais"];
const AFTER = ["Portal único", "Demandas organizadas", "Protocolos", "Prazos", "Status", "Histórico", "Documentos centralizados", "Acompanhamento digital"];

export function BeforeAfter() {
  return (
    <section className="bg-white py-20 sm:py-28" data-testid="lp-before-after">
      <Container>
        <Reveal className="text-center max-w-2xl mx-auto"><Eyebrow>Antes e depois</Eyebrow><H2>Seu Departamento Pessoal agora é digital.</H2></Reveal>
        <div className="mt-14 grid md:grid-cols-[1fr_auto_1fr] gap-5 items-stretch">
          <Reveal className="rounded-2xl border border-slate-200 bg-[#F8FAFC] p-8"><div className="font-mono text-xs uppercase tracking-widest text-slate-500">Antes</div>
            <ul className="mt-6 flex flex-wrap gap-2">{BEFORE.map((b) => <li key={b} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-3.5 py-2 text-sm text-slate-500"><X className="h-3.5 w-3.5 text-slate-400" />{b}</li>)}</ul></Reveal>
          <div className="hidden md:flex items-center"><ArrowRight className="h-8 w-8 text-[#0B6FC2]" /></div>
          <Reveal delay={100} className="rounded-2xl bg-[#0B6FC2] p-8 text-white"><div className="font-mono text-xs uppercase tracking-widest text-sky-100">Com a DPdigi</div>
            <ul className="mt-6 flex flex-wrap gap-2">{AFTER.map((a) => <li key={a} className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3.5 py-2 text-sm font-medium"><Check className="h-3.5 w-3.5" />{a}</li>)}</ul></Reveal>
        </div>
      </Container>
    </section>);
}

const SEC = ["Controle de acesso por usuário e perfil", "Separação dos dados por empresa", "Histórico de operações", "Registro de alterações", "Rastreabilidade por protocolo", "Documentos organizados", "Registros de processamento"];

export function Security() {
  return (
    <section className="bg-[#063C6D] py-20 sm:py-28 text-white" data-testid="lp-security">
      <Container className="grid lg:grid-cols-12 gap-12 items-center">
        <Reveal className="lg:col-span-5"><ShieldCheck className="h-8 w-8 text-sky-300" /><H2 light className="mt-6">Informação de DP precisa de controle.</H2>
          <p className="mt-5 text-lg text-sky-100/80">Do pedido ao resultado, tudo registrado.</p>
          <div className="mt-8"><PrimaryCta source="security" testid="lp-security-cta" light /></div></Reveal>
        <ul className="lg:col-span-7 grid sm:grid-cols-2 gap-3">{SEC.map((s, i) => (
          <Reveal as="li" key={s} delay={i * 50} className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.04] px-5 py-4"><Check className="h-4 w-4 text-sky-300 shrink-0" />{s}</Reveal>))}</ul>
      </Container>
    </section>);
}
